import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Users, MapPin, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../data/translations';
import { SEO } from '../components/SEO';

export default function TeamPage() {
  const { language } = useLanguage();
  const t = useTranslation(language);
  return (
    <main className="pt-20" data-testid="team-page">
      <SEO
        title="Über uns – BENI Gipsergeschäft GmbH Aesch"
        path="/team"
        description="Lernen Sie BENI Gipsergeschäft GmbH kennen. Unternehmen seit 2019 in Aesch. Ihr zuverlässiger Partner für Gipser-, Maler- und Trockenbauarbeiten in der Region Basel."
        keywords="Über uns Gipser Aesch, BENI Gipsergeschäft Team, Gipser Unternehmen Basel-Landschaft, Handwerksbetrieb Aesch, Gipser Familienbetrieb Basel"
        breadcrumbs={[{ name: 'Startseite', path: '/' }, { name: 'Über uns', path: '/team' }]}
      />
      <section className="py-20 md:py-28 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="team-page-title">{t.team.title}</h1>
            <p className="text-xl text-gray-300 max-w-2xl">{t.team.subtitle}</p>
            <div className="w-20 h-1 bg-[#E31C23] mt-8" />
          </motion.div>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_a13cc131-3351-41ce-87d0-56be3a0fcef6/artifacts/dbt3hpvl_PHOTO-2026-04-10-17-17-48.jpg" alt="BENI Gipsergeschäft Firmenfahrzeuge" className="w-full h-auto" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                <div className="text-white font-heading font-bold text-lg">Beni Gipsergeschäft GmbH</div>
                <div className="text-white/70 text-sm">Unternehmen seit 2019 | Aesch, Basel</div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6">{t.team.about_title}</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>{t.team.about_text}</p>
                <p>{t.team.values}</p>
                <p className="text-[#E31C23] font-medium text-lg">{t.team.apprentice}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="py-16 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: language === 'de' ? 'Vielfalt Planung' : 'Diverse Planning', desc: language === 'de' ? 'Umbau, Neubau, Sanierungen' : 'Renovation, new build, refurbishment', color: '#E31C23' },
              { icon: Users, title: language === 'de' ? 'Seit 2019' : language === 'en' ? 'Since 2019' : 'Depuis 2019', desc: language === 'de' ? 'Ihr Partner in der Region' : 'Your regional partner', color: '#00A651' },
              { icon: MapPin, title: language === 'de' ? 'Region Basel' : 'Basel Region', desc: 'Aesch, Reinach, Münchenstein...', color: '#E31C23' },
              { icon: Clock, title: language === 'de' ? 'Zuverlässig' : 'Reliable', desc: language === 'de' ? 'Termingerechte Ausführung' : 'On-time delivery', color: '#C41230' }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white p-8 border border-gray-100 text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}><item.icon className="w-8 h-8" style={{ color: item.color }} /></div>
                <h3 className="font-heading text-xl font-bold text-[#1A1A1A] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Projekttypen */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-8">{language === 'de' ? 'Wir arbeiten für' : 'We work for'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: language === 'de' ? 'Ein- und Mehrfamilienhäuser' : 'Residential Buildings', desc: language === 'de' ? 'Umbau, Neubau, Sanierungen' : 'Renovation, new build, refurbishment' },
              { title: language === 'de' ? 'Büro- und Verwaltungsgebäude' : 'Office Buildings', desc: language === 'de' ? 'Professioneller Innenausbau' : 'Professional interior finishing' },
              { title: language === 'de' ? 'Gewerbe- und Industriebauten' : 'Commercial Buildings', desc: language === 'de' ? 'Grossprojekte und Sanierungen' : 'Large projects and refurbishments' }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-[#F8F9FA] p-8 border border-gray-100">
                <h3 className="font-heading text-lg font-bold text-[#1A1A1A] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 bg-[#E31C23]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">{t.team.cta}</h2>
            <Link to="/kontakt" className="inline-flex items-center space-x-2 bg-white text-[#E31C23] px-8 py-4 font-semibold hover:bg-gray-100 transition-colors" data-testid="team-contact-cta"><span>{t.nav.contact}</span><ArrowRight className="w-5 h-5" /></Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
